import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router'
import { AuthProvider } from '@/auth/AuthContext'
import { RequireAuth } from '@/auth/RequireAuth'
import { ChannelProvider } from '@/context/ChannelContext'
import { DesktopShell } from '@/shell/DesktopShell'
import { LoginPage } from '@/pages/LoginPage'
import { DashboardPage } from '@/pages/DashboardPage'
import { BillingPage } from '@/pages/BillingPage'
import { AnalyticsPage } from '@/pages/AnalyticsPage'
import { WelcomeIntroPage } from '@/pages/onboarding/WelcomeIntroPage'
import { OverviewPage } from '@/pages/onboarding/OverviewPage'
import { SourcePage } from '@/pages/onboarding/SourcePage'
import { ConnectSubscriberPage } from '@/pages/onboarding/ConnectSubscriberPage'
import { ConnectAdminPage } from '@/pages/onboarding/ConnectAdminPage'
import { ConnectDonePage } from '@/pages/onboarding/ConnectDonePage'
import { BranchPage } from '@/pages/onboarding/BranchPage'
import { TariffPage } from '@/pages/onboarding/TariffPage'
import { OnboardingFinalPage } from '@/pages/onboarding/OnboardingFinalPage'
import { YandexConnectPage } from '@/pages/onboarding/YandexConnectPage'
import { YandexDonePage } from '@/pages/onboarding/YandexDonePage'
import { RequisitesPage } from '@/pages/onboarding/RequisitesPage'
import { AdGenPreparePage } from '@/pages/onboarding/AdGenPreparePage'
import { CreativesEditorPage } from '@/pages/onboarding/CreativesEditorPage'
import { CampaignsReadyPage } from '@/pages/onboarding/CampaignsReadyPage'
import { OnboardingResetPage } from '@/pages/onboarding/OnboardingResetPage'
import { ShowcaseConsentPage as OnbShowcaseConsentPage } from '@/pages/onboarding/ShowcaseConsentPage'
import { ShowcaseRulesPage as OnbShowcaseRulesPage } from '@/pages/onboarding/ShowcaseRulesPage'
import { getOnboardingState, STEP_TO_ROUTE, type OnboardingStep } from '@/api/onboarding'
import { getPollsFlow } from '@/onboarding/pollsFlow'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { ShowcaseRoute } from '@/pages/showcase/ShowcaseRoute'
import { OverviewPage as ShowcaseOverviewPage } from '@/pages/showcase/OverviewPage'
import { PostsPage as ShowcasePostsPage } from '@/pages/showcase/PostsPage'
import { SettingsPage as ShowcaseSettingsPage } from '@/pages/showcase/SettingsPage'
import { PostCardPage as ShowcasePostCardPage } from '@/pages/showcase/PostCardPage'
import { RequireShowcaseAccess } from '@/showcase/RequireShowcaseAccess'
import { RequirePollsAccess } from '@/polls/RequirePollsAccess'
import { LabMembershipOverlay } from '@/polls/LabMembershipOverlay'
import { PollsListPage } from '@/pages/polls/PollsListPage'
import { CreatePollPage } from '@/pages/polls/CreatePollPage'
import { PollResultsPage } from '@/pages/polls/PollResultsPage'
import { PollsChannelPickerPage } from '@/pages/polls/PollsChannelPickerPage'
import { PollsWizardProvider } from '@/onboarding/PollsWizardContext'
import { PollsWizardTemplatePage } from '@/pages/onboarding/polls/PollsWizardTemplatePage'
import { PollsWizardPreviewPage } from '@/pages/onboarding/polls/PollsWizardPreviewPage'
import { PollsWizardSuccessPage } from '@/pages/onboarding/polls/PollsWizardSuccessPage'

// Y.Metrika SPA pageview tracker — глобальное window.ym объявлено в @/lib/analytics
const YM_COUNTER = 96625866

function YmTracker() {
  const location = useLocation()
  useEffect(() => {
    if (typeof window.ym === 'function') {
      window.ym(YM_COUNTER, 'hit', location.pathname + location.search)
    }
  }, [location.pathname, location.search])
  return null
}

function ProtectedLayout() {
  return (
    <RequireAuth>
      <ChannelProvider>
        <DesktopShell>
          <Outlet />
        </DesktopShell>
      </ChannelProvider>
    </RequireAuth>
  )
}

const TOKEN_STEPS: OnboardingStep[] = ['connect-subscriber', 'connect-admin', 'connect-done']

function OnboardingResumeGate() {
  const navigate = useNavigate()
  const location = useLocation()
  const [resumed, setResumed] = useState(false)

  useEffect(() => {
    if (resumed) return
    if (location.pathname !== '/onboarding') {
      setResumed(true)
      return
    }
    let cancelled = false
    getOnboardingState()
      .then((state) => {
        if (cancelled) return
        setResumed(true)
        if (!state.currentStep || state.currentStep === 'welcome-intro') return
        if (TOKEN_STEPS.includes(state.currentStep)) {
          const pollsFlow = getPollsFlow()
          if (pollsFlow.active && state.currentStep === 'connect-done') {
            if (state.channelUUID) {
              const qs = new URLSearchParams({ channelUUID: state.channelUUID })
              if (pollsFlow.ref) qs.set('ref', pollsFlow.ref)
              navigate(`/onboarding/polls/template?${qs.toString()}`, { replace: true })
            } else {
              const qs = new URLSearchParams()
              if (pollsFlow.ref) qs.set('ref', pollsFlow.ref)
              navigate(`/onboarding/source${qs.toString() ? `?${qs.toString()}` : ''}`, { replace: true })
            }
            return
          }
          navigate('/onboarding/source', { replace: true })
          return
        }
        const target = STEP_TO_ROUTE[state.currentStep]
        if (target && target !== '/onboarding') navigate(target, { replace: true })
      })
      .catch(() => {
        if (!cancelled) setResumed(true)
      })
    return () => {
      cancelled = true
    }
  }, [resumed, location.pathname, navigate])

  return <Outlet />
}

function OnboardingLayout() {
  return (
    <RequireAuth>
      <ChannelProvider>
        <OnboardingResumeGate />
      </ChannelProvider>
    </RequireAuth>
  )
}

// Обрабатывает ошибочно раскатанные magic-ссылки формата /magic/<token>.
// Правильный обработчик — AuthContext:46 перехватывает ?magic= из query.
// Дублирует nginx-редирект (tgapp-lk/nginx.conf) как SPA-fallback.
function MagicRedirect() {
  const { token } = useParams<{ token: string }>()
  return <Navigate to={`/onboarding?magic=${encodeURIComponent(token || '')}`} replace />
}

export function App() {
  return (
    <AuthProvider>
      <YmTracker />
      <Routes>
        {/* Fix для ошибочно раскатанных magic-ссылок формата /magic/<token>.
            Реальный обработчик — AuthContext перехватывает ?magic= из query.
            Дублирует nginx-редирект (tgapp-lk/nginx.conf) — SPA-fallback на
            случай если nginx-правило удалим или юзер попадёт в кеш прошлой
            версии index.html без корректной nginx-логики. */}
        <Route path="/magic/:token" element={<MagicRedirect />} />
        <Route path="/login" element={<LoginPage />} />

        <Route element={<OnboardingLayout />}>
          <Route path="/onboarding/reset" element={<OnboardingResetPage />} />
          <Route path="/onboarding" element={<WelcomeIntroPage />} />
          {/* Polls onboarding alias — intent `polls:onboard-channel` (n=0 branch)
              из bot welcome ведёт сюда. Тот же WelcomeIntroPage: emit
              `poll_onboarding_opened` + magic-login handler. */}
          <Route path="/onboarding/channel" element={<WelcomeIntroPage />} />
          <Route path="/onboarding/overview" element={<OverviewPage />} />
          <Route path="/onboarding/source" element={<SourcePage />} />
          <Route path="/onboarding/connect-subscriber/:token" element={<ConnectSubscriberPage />} />
          <Route path="/onboarding/connect-admin/:token" element={<ConnectAdminPage />} />
          <Route path="/onboarding/connect-done/:token" element={<ConnectDonePage />} />
          <Route path="/onboarding/branch" element={<BranchPage />} />
          <Route path="/onboarding/showcase-consent" element={<OnbShowcaseConsentPage />} />
          <Route path="/onboarding/showcase-rules" element={<OnbShowcaseRulesPage />} />
          <Route path="/onboarding/tariff" element={<TariffPage />} />
          <Route path="/onboarding/final" element={<OnboardingFinalPage />} />
          <Route path="/onboarding/yandex-connect" element={<YandexConnectPage />} />
          <Route path="/onboarding/yandex-done" element={<YandexDonePage />} />
          <Route path="/onboarding/requisites" element={<RequisitesPage />} />
          <Route path="/onboarding/ad-gen-prepare" element={<AdGenPreparePage />} />
          <Route path="/onboarding/creatives-editor" element={<CreativesEditorPage />} />
          <Route path="/onboarding/campaigns-ready" element={<CampaignsReadyPage />} />
          {/* Polls-onboarding wizard (design_v2_pool 18/19/20). Fullscreen без
              DesktopShell sidebar — часть онбординга. Провайдер оборачивает 3
              шага — state (template, question, options) sticky в SPA. */}
          <Route
            path="/onboarding/polls/*"
            element={
              <RequirePollsAccess>
                <LabMembershipOverlay>
                  <PollsWizardProvider>
                    <Routes>
                      <Route path="template" element={<PollsWizardTemplatePage />} />
                      <Route path="preview" element={<PollsWizardPreviewPage />} />
                      <Route path="success" element={<PollsWizardSuccessPage />} />
                      <Route path="*" element={<Navigate to="/onboarding/polls/template" replace />} />
                    </Routes>
                  </PollsWizardProvider>
                </LabMembershipOverlay>
              </RequirePollsAccess>
            }
          />
        </Route>

        <Route element={<ProtectedLayout />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/billing" element={<BillingPage />} />
          {/* Showcase (Витрина) — Phase 1 видна только whitelist'у в useShowcaseAccess */}
          <Route
            path="/showcase"
            element={
              <RequireShowcaseAccess>
                <ShowcaseRoute />
              </RequireShowcaseAccess>
            }
          >
            <Route index element={<ShowcaseOverviewPage />} />
            <Route path="posts" element={<ShowcasePostsPage />} />
            <Route path="posts/:postId" element={<ShowcasePostCardPage />} />
            <Route path="settings" element={<ShowcaseSettingsPage />} />
          </Route>
          {/* Polls (Опросы) — Phase 1 видна только whitelist'у в usePollsAccess.
              Lab-membership overlay поверх /polls* — при isMember=false закрывает
              взаимодействие модалкой (§2.0). Fail-open при недоступности MAX API
              внутри useLabMembership — раздел не блокируется. */}
          <Route
            path="/polls"
            element={
              <RequirePollsAccess>
                <LabMembershipOverlay>
                  <PollsListPage />
                </LabMembershipOverlay>
              </RequirePollsAccess>
            }
          />
          <Route
            path="/polls/new"
            element={
              <RequirePollsAccess>
                <LabMembershipOverlay>
                  <CreatePollPage />
                </LabMembershipOverlay>
              </RequirePollsAccess>
            }
          />
          {/* Utility route — channel-picker (не в magic-flow, B сам разруливает). */}
          <Route
            path="/polls/channel-picker"
            element={
              <RequirePollsAccess>
                <LabMembershipOverlay>
                  <PollsChannelPickerPage />
                </LabMembershipOverlay>
              </RequirePollsAccess>
            }
          />
          <Route
            path="/polls/:pollId"
            element={
              <RequirePollsAccess>
                <LabMembershipOverlay>
                  <PollResultsPage />
                </LabMembershipOverlay>
              </RequirePollsAccess>
            }
          />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AuthProvider>
  )
}
